import moment from 'moment'
import {
  axios,
  FindPatientsQuery,
  getConfigs,
  IRequestConfig,
  MemberDto,
  PatientDto,
} from './api'
import { AppointmentMinDTO, DocumentDTO } from './dtos'
import { PaginatedDto } from './extendedType'

export interface PatientMergeHistoryDto {
  id: number
  sourcePatient: PatientDto
  targetPatient: PatientDto
  createdAt: Date
  updatedAt: Date
  mergedBy: MemberDto
  reversedBy: MemberDto
  reversedAt: Date
}

export class PatientServiceV2 {
  private static readonly endpoint = 'patient'

  static findMany(query: FindPatientsQuery): Promise<PaginatedDto<PatientDto>> {
    return new Promise((resolve, reject) => {
      const configs: IRequestConfig = getConfigs(
        'get',
        'application/json',
        `${this.endpoint}/v2`,
        {},
      )
      configs.params = query
      axios(configs, resolve, reject)
    })
  }

  static mergePatient(
    sourcePatientId: number,
    targetPatientId: number,
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const configs: IRequestConfig = getConfigs(
        'post',
        'application/json',
        `${this.endpoint}/${sourcePatientId}/merge`,
        {},
      )
      configs.data = {
        targetPatientId,
      }
      axios(configs, resolve, reject)
    })
  }

  static cancelPatientMerge(pId: number, mergeId: number): Promise<void> {
    return new Promise((resolve, reject) => {
      const configs: IRequestConfig = getConfigs(
        'patch',
        'application/json',
        `${this.endpoint}/${pId}/cancel-merge/${mergeId}`,
        {},
      )
      axios(configs, resolve, reject)
    })
  }

  static findMergeHistory(
    patientId: number,
  ): Promise<PatientMergeHistoryDto[]> {
    return new Promise((resolve, reject) => {
      const configs: IRequestConfig = getConfigs(
        'get',
        'application/json',
        `${this.endpoint}/${patientId}/merge-history`,
        {},
      )
      axios(configs, resolve, reject)
    })
  }

  static getDocuments(
    patientId: number,
    params?: { page?: number; take?: number },
  ): Promise<PaginatedDto<DocumentDTO>> {
    return new Promise((resolve, reject) => {
      const configs: IRequestConfig = getConfigs(
        'get',
        'application/json',
        `${this.endpoint}/${patientId}/get-documents`,
        {},
      )
      if (params) {
        configs.params = params
      }
      axios(configs, resolve, reject)
    })
  }

  static getByIdentifier(
    identifier: string,
    ssn?: string | null,
  ): Promise<PatientDto | null> {
    return new Promise((resolve, reject) => {
      const configs: IRequestConfig = getConfigs(
        'get',
        'application/json',
        `${this.endpoint}/by-identifier/${encodeURIComponent(identifier)}`,
        {},
      )
      if (ssn) {
        configs.params = { ssn }
      }
      axios(configs, resolve, reject)
    })
  }

  static getPatientStats(
    patientId: number,
  ): Promise<{ sold: number; examCount: number }> {
    return new Promise((resolve, reject) => {
      const config: IRequestConfig = getConfigs(
        'get',
        'application/json',
        `${this.endpoint}/${patientId}/stats`,
        {},
      )
      axios(config, resolve, reject)
    })
  }

  public static getIdentifier(
    ssn: string,
    firstName: string,
    lastName: string,
    birthDate: string,
  ): string {
    if (!ssn || !firstName || !lastName || !birthDate) {
      return ''
    }

    const ssnWithoutSpaces = ssn.replace(/\s/g, '').trim()
    if (!ssnWithoutSpaces) return ''

    const m = moment(
      birthDate,
      ['YYYYMMDD', 'DD-MM-YYYY', 'YYYY-MM-DD', moment.ISO_8601],
      true,
    )
    if (!m.isValid()) return ''
    const formattedBirthDate = m.format('DD-MM-YYYY')

    return `${ssnWithoutSpaces}-${firstName.toLowerCase().trim()}-${lastName
      .toLowerCase()
      .trim()}-${formattedBirthDate}`
  }

  static getNoShowAppointments(
    patientId: number,
  ): Promise<AppointmentMinDTO[]> {
    return new Promise((resolve, reject) => {
      const config: IRequestConfig = getConfigs(
        'get',
        'application/json',
        `appointments/no-shows?patientId=${patientId}`,
        {},
      )
      axios(config, resolve, reject)
    })
  }
}
